/**
 * GlobalNav — the role-aware primary navigation, rendered in the masthead.
 *
 * Resolves the signed-in role once (cached) and shows the right destinations:
 *   - ADMINISTRATOR / ESTIMATOR: Lead queue, Quote on behalf, Contractors, Dashboard
 *   - CLIENT: My projects, Start a project
 *   - CONTRACTOR: My jobs (placeholder — the portal is Phase 5)
 * Plus Log out for any signed-in user.
 *
 * Desktop: inline links. Mobile: a hamburger that opens a slide-in drawer.
 * Hidden when signed out (the nav has nothing to offer on the sign-in flow).
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getRole, cachedRole, clearToken, type Role } from '../lib/api';

interface NavItem {
  to: string;
  label: string;
}

function itemsForRole(role: Role): NavItem[] {
  switch (role) {
    case 'ADMINISTRATOR':
    case 'ESTIMATOR':
      return [
        { to: '/admin/dashboard', label: 'Dashboard' },
        { to: '/admin/leads', label: 'Lead queue' },
        { to: '/admin', label: 'Intake' },
        { to: '/admin/manage-contractors', label: 'Contractors' },
      ];
    case 'CONTRACTOR':
      return [{ to: '/contractor', label: 'My jobs' }];
    case 'CLIENT':
    default:
      return [
        { to: '/dashboard', label: 'My projects' },
        { to: '/intake', label: 'Add a property' },
      ];
  }
}

export function GlobalNav(): JSX.Element | null {
  const navigate = useNavigate();
  const location = useLocation();
  const [role, setRole] = useState<Role | null>(cachedRole());
  const [open, setOpen] = useState(false);

  // Re-resolve the role whenever the route changes (covers login → first
  // authenticated page, and logout clearing it). getRole is cached, so this is
  // a cheap no-op once resolved.
  useEffect(() => {
    let active = true;
    void getRole().then((r) => {
      if (active) setRole(r);
    });
    return () => {
      active = false;
    };
  }, [location.pathname]);

  // Close the mobile drawer on any navigation.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  if (!role) return null; // signed out — nothing to navigate

  const items = itemsForRole(role);

  function logout(): void {
    clearToken();
    setRole(null);
    navigate('/');
  }

  function isActive(to: string): boolean {
    // Exact match. The admin routes are siblings (/admin, /admin/dashboard,
    // /admin/leads), so a prefix match would light up multiple links at once.
    return location.pathname === to;
  }

  return (
    <>
      {/* Desktop inline links */}
      <nav className="app-nav" aria-label="Primary">
        {items.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            className={`app-nav__link${isActive(it.to) ? ' app-nav__link--active' : ''}`}
          >
            {it.label}
          </Link>
        ))}
        <button type="button" className="app-nav__link app-nav__logout" onClick={logout}>
          Log out
        </button>
      </nav>

      {/* Mobile hamburger */}
      <button
        type="button"
        className="app-nav__toggle"
        aria-label={open ? 'Close menu' : 'Open menu'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="app-nav__bars" aria-hidden="true" />
      </button>

      {/* Mobile slide-in drawer + backdrop */}
      {open ? <div className="app-drawer__backdrop" onClick={() => setOpen(false)} /> : null}
      <aside className={`app-drawer${open ? ' app-drawer--open' : ''}`} aria-hidden={!open}>
        <nav className="app-drawer__nav" aria-label="Primary">
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              className={`app-drawer__link${isActive(it.to) ? ' app-drawer__link--active' : ''}`}
            >
              {it.label}
            </Link>
          ))}
          <button type="button" className="app-drawer__link app-drawer__logout" onClick={logout}>
            Log out
          </button>
        </nav>
      </aside>
    </>
  );
}
